import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import RelatedGuides from '../../components/common/RelatedGuides';
import { useState } from 'react';
import {
  hobbitenangoCost,
  transportOptions,
  hobbitenangoCerroComparison,
  whatToDo,
  proTips,
  faqs,
  itineraryIdeas
} from './data';

export default function HobbitenangoPag() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Day Trips', path: '/day-trips-from-antigua-guatemala' },
    { label: 'Hobbitenango', path: '/hobbitenango-antigua-guatemala' }
  ];

  const relatedGuides = [
    {
      title: 'Things to Do in Antigua Guatemala',
      path: '/things-to-do-in-antigua-guatemala',
      description: 'Complete guide to the best activities and attractions'
    },
    {
      title: 'Day Trips from Antigua',
      path: '/day-trips-from-antigua-guatemala',
      description: 'Explore the best day trips and excursions'
    },
    {
      title: 'Acatenango Volcano Hike',
      path: '/acatenango-hike-from-antigua',
      description: 'Epic overnight volcano trek with lava views'
    },
    {
      title: 'Best Time to Visit Antigua',
      path: '/best-time-to-visit-antigua-guatemala',
      description: 'Weather guide and seasonal travel tips'
    },
    {
      title: 'Antigua 3-Day Itinerary',
      path: '/antigua-guatemala-3-day-itinerary',
      description: 'Perfect weekend itinerary for first-time visitors'
    },
    {
      title: 'Is Antigua Safe?',
      path: '/is-antigua-guatemala-safe',
      description: 'Safety tips and practical advice for travelers'
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Hobbitenango Antigua Guatemala: Complete Visitor Guide (2026)",
        "description": "Complete guide to visiting Hobbitenango from Antigua Guatemala. Entrance fees, how to get there, best photo spots, and insider tips for 2026.",
        "image": "https://public.readdy.ai/ai/img_res/bab5c03bd7d09c900404b00f36b19113.jpg",
        "author": {
          "@type": "Organization",
          "name": "La Antigua Guatemala Travel Guide"
        },
        "publisher": {
          "@type": "Organization",
          "name": "La Antigua Guatemala Travel Guide",
          "logo": {
            "@type": "ImageObject",
            "url": "https://la-antigua-guatemala.com/logo.png"
          }
        },
        "datePublished": "2024-01-15",
        "dateModified": "2026-01-15",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://la-antigua-guatemala.com/hobbitenango-antigua-guatemala"
        },
        "inLanguage": "en-US"
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://la-antigua-guatemala.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Day Trips",
            "item": "https://la-antigua-guatemala.com/day-trips-from-antigua-guatemala"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Hobbitenango",
            "item": "https://la-antigua-guatemala.com/hobbitenango-antigua-guatemala"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Hobbitenango Antigua Guatemala: Complete Guide (2026)"
        description="Complete guide to Hobbitenango from Antigua. Entrance fees, transport options, best photo spots, and insider tips. Plan your visit to this unique mountain park."
        canonical="https://la-antigua-guatemala.com/hobbitenango-antigua-guatemala"
        keywords="hobbitenango antigua guatemala, hobbitenango park, hobbitenango guatemala, how to get to hobbitenango, hobbitenango entrance fee, hobbitenango restaurant, antigua guatemala day trip, things to do in antigua guatemala, volcano views antigua"
        type="article"
        schema={schemaData}
        lastModified="2026-01-15"
        ogImage={{
          url: "https://readdy.ai/api/search-image?query=Hobbitenango%20giant%20hand%20viewpoint%20overlooking%20three%20volcanoes%20Agua%20Fuego%20Acatenango%20with%20colorful%20hobbit%20houses%20in%20mountain%20eco%20park%20Guatemala%20dramatic%20landscape%20photography%20clear%20blue%20sky&width=1200&height=630&seq=hobb-hero-001&orientation=landscape",
          width: 1200,
          height: 630
        }}
      />

      <Navbar />

      {/* Hero Section */}
      <div className="relative h-[500px] w-full">
        <img
          src="https://readdy.ai/api/search-image?query=Hobbitenango%20giant%20hand%20viewpoint%20overlooking%20three%20volcanoes%20Agua%20Fuego%20Acatenango%20with%20colorful%20hobbit%20houses%20in%20mountain%20eco%20park%20Guatemala%20dramatic%20landscape%20photography%20clear%20blue%20sky&width=1920&height=800&seq=hobb-hero-002&orientation=landscape"
          alt="Hobbitenango giant hand viewpoint with volcano views"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Hobbitenango Antigua Guatemala: Complete Visitor Guide (2026)
            </h1>
            <p className="text-lg md:text-xl">
              Everything you need to know about visiting this unique mountain park with volcano views
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Quick Take Box */}
        <div className="bg-teal-50 border-l-4 border-teal-500 p-6 my-8 rounded-r-lg">
          <h2 className="text-2xl font-bold text-teal-900 mb-4">Quick Take</h2>
          <div className="grid md:grid-cols-2 gap-4 text-teal-900">
            <div>
              <p className="font-semibold">Distance from Antigua:</p>
              <p>6 km northwest (25-40 min drive)</p>
            </div>
            <div>
              <p className="font-semibold">Entrance Fee:</p>
              <p>Q100 (~$13 USD) + Q50 restaurant credit</p>
            </div>
            <div>
              <p className="font-semibold">Time Needed:</p>
              <p>2-4 hours</p>
            </div>
            <div>
              <p className="font-semibold">Best For:</p>
              <p>Instagram photos, families, volcano views</p>
            </div>
            <div>
              <p className="font-semibold">Elevation:</p>
              <p>~2,500 meters (8,200 feet)</p>
            </div>
            <div>
              <p className="font-semibold">Skip If:</p>
              <p>Cloudy weather, tight budget, prefer natural settings</p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="prose max-w-none mb-12">
          <p className="text-lg leading-relaxed mb-4">
            <strong>Hobbitenango Antigua Guatemala</strong> has become one of the most popular day trips from Antigua, and for good reason. This whimsical mountain eco-park combines Instagram-worthy photo spots with genuine volcano views, making it a unique addition to any <a href="/antigua-guatemala-travel-guide" className="text-teal-600 hover:text-teal-700">Antigua Guatemala travel guide</a>.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            Perched on a mountainside northwest of Antigua, Hobbitenango features colorful hobbit-style houses, a giant hand sculpture reaching over the valley, sky swings, nature trails, and a restaurant with panoramic views of Volcán de Agua, Volcán de Fuego, and Volcán Acatenango.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            While some travelers dismiss it as too touristy or commercialized, others find it a fun, family-friendly experience that offers something different from the typical colonial architecture and volcano hikes. Whether Hobbitenango is worth your time depends on what you're looking for—and whether the weather cooperates.
          </p>
        </div>

        {/* What is Hobbitenango */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">What is Hobbitenango?</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango is a privately-owned eco-park and adventure destination located in the mountains above Antigua Guatemala. The name combines "hobbit" (inspired by J.R.R. Tolkien's fantasy world) with "Tenango," a Nahuatl word meaning "place of."
          </p>

          <p className="text-lg leading-relaxed mb-4">
            The park opened in recent years and quickly gained popularity on social media thanks to its most iconic feature: a massive hand sculpture that appears to reach out over the valley, creating a dramatic photo opportunity with three volcanoes in the background.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            Beyond the giant hand, Hobbitenango features colorful hobbit-style houses built into the hillside, multiple swings positioned at the mountain's edge, nature trails through pine forests, and a full-service restaurant. The park sits at approximately 2,500 meters elevation, offering cooler temperatures and expansive views when weather permits.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            It's designed as a half-day experience that combines outdoor recreation, photography, and dining—making it popular with families, couples, and Instagram enthusiasts looking for unique content.
          </p>
        </section>

        {/* Where is Hobbitenango Located */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Where is Hobbitenango Located?</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango is located approximately 6 kilometers northwest of Antigua's central plaza, in the mountains above the town. The park sits at roughly 2,500 meters (8,200 feet) elevation, about 300 meters higher than Antigua itself.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            The drive takes 25-40 minutes depending on traffic and your starting point in Antigua. The road is paved but steep and winding in sections, with some narrow passages where vehicles need to navigate carefully around each other.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            From Hobbitenango's viewpoints, you can see Antigua spread out in the valley below, with Volcán de Agua dominating the southern horizon. On clear days, Volcán de Fuego and Volcán Acatenango are visible to the west and southwest.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            The park's elevated position means it's often cooler and windier than Antigua, especially in the morning and late afternoon. It also means clouds can roll in and obscure the volcano views, particularly during rainy season (May-October).
          </p>
        </section>

        {/* How to Get There */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">How to Get to Hobbitenango from Antigua</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            You have several options for reaching Hobbitenango from Antigua. Most visitors take an organized shuttle or private driver, though hiking and taxi are also possible.
          </p>

          <div className="overflow-x-auto my-8">
            <table className="w-full border-collapse bg-white shadow-sm rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-teal-600 text-white">
                  <th className="px-4 py-3 text-left font-semibold">Method</th>
                  <th className="px-4 py-3 text-left font-semibold">Cost</th>
                  <th className="px-4 py-3 text-left font-semibold">Duration</th>
                  <th className="px-4 py-3 text-left font-semibold">Pros</th>
                  <th className="px-4 py-3 text-left font-semibold">Cons</th>
                </tr>
              </thead>
              <tbody>
                {transportOptions.map((option, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-4 py-3 font-medium">{option.method}</td>
                    <td className="px-4 py-3">{option.cost}</td>
                    <td className="px-4 py-3">{option.duration}</td>
                    <td className="px-4 py-3 text-sm">{option.pros}</td>
                    <td className="px-4 py-3 text-sm">{option.cons}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="text-2xl font-bold mb-4 mt-8">Organized Shuttle (Recommended for Most)</h3>
          <p className="text-lg leading-relaxed mb-4">
            Many tour operators and hostels in Antigua offer shuttle service to Hobbitenango. These typically depart in the morning (around 9-10 AM) and return in the early afternoon, giving you 2-3 hours at the park.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            Cost is usually Q50-100 per person round-trip. This is the most convenient option if you don't need flexible timing and want to avoid arranging your own transport.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Private Driver</h3>
          <p className="text-lg leading-relaxed mb-4">
            Hiring a private driver gives you complete flexibility over departure time and how long you stay. Expect to pay Q200-300 for a round-trip, including waiting time. Your hotel or hostel can arrange this, or you can book through local tour operators.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Uber or Taxi</h3>
          <p className="text-lg leading-relaxed mb-4">
            Uber operates in Antigua and can take you to Hobbitenango for approximately Q80-120 one-way. However, getting a return ride can be challenging since there aren't always drivers available at the park. If you choose this option, arrange your pickup time in advance or be prepared to wait.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Hiking</h3>
          <p className="text-lg leading-relaxed mb-4">
            It's technically possible to hike from Antigua to Hobbitenango, but it's not recommended for most visitors. The route is about 6 km uphill on steep roads with limited sidewalks and vehicle traffic. It takes 2-3 hours one-way and requires good fitness.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            If you're determined to hike, start early in the morning, bring plenty of water, wear good shoes, and be cautious of traffic. Most people find the shuttle or driver option more practical.
          </p>
        </section>

        {/* Entrance Fee */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Hobbitenango Entrance Fee (2026 Update)</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            As of 2026, the entrance fee to Hobbitenango is <strong>Q100 per person</strong> (approximately $13 USD). This includes access to all viewpoints, trails, photo spots, and facilities within the park.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            Your entrance ticket also includes <strong>Q50 credit</strong> to use at the on-site restaurant. This credit can be applied toward food, drinks, or coffee, effectively reducing the net cost of admission to Q50 (~$6.50 USD) if you plan to eat there.
          </p>

          <div className="overflow-x-auto my-8">
            <table className="w-full border-collapse bg-white shadow-sm rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-teal-600 text-white">
                  <th className="px-4 py-3 text-left font-semibold">Item</th>
                  <th className="px-4 py-3 text-left font-semibold">Cost</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody>
                {hobbitenangoCost.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-4 py-3 font-medium">{item.item}</td>
                    <td className="px-4 py-3">{item.cost}</td>
                    <td className="px-4 py-3 text-sm">{item.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="text-2xl font-bold mb-4 mt-8">Opening Hours</h3>
          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango typically opens at <strong>8:00 AM</strong> and closes around <strong>5:00-6:00 PM</strong>. Hours may vary seasonally or during holidays, so it's worth checking ahead if you're visiting during peak times or rainy season.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            The park is open seven days a week, though weekends tend to be busier with both tourists and local families. Weekday mornings offer the quietest experience.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Payment Options</h3>
          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango accepts both cash (Quetzales and US dollars) and credit cards. However, it's always wise to bring some cash as backup, especially for tips or small purchases.
          </p>
        </section>

        {/* What to Do */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">What to Do at Hobbitenango</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango offers a variety of activities and photo opportunities spread across the mountain property. Here's what you can expect:
          </p>

          <div className="grid md:grid-cols-2 gap-6 my-8">
            {whatToDo.map((activity, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                  <i className={`${activity.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">{activity.title}</h3>
                <p className="text-gray-700 leading-relaxed">{activity.description}</p>
              </div>
            ))}
          </div>

          <p className="text-lg leading-relaxed mb-4">
            The park is designed for leisurely exploration. There's no set route—you can wander between viewpoints, take photos, relax at the restaurant, and enjoy the mountain atmosphere at your own pace.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            Most visitors spend 2-4 hours at Hobbitenango, though you could easily fill a half-day if you're taking your time with photos, enjoying a meal, and exploring all the trails.
          </p>
        </section>

        {/* Best Time to Visit */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Best Time to Visit Hobbitenango</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            Timing your visit to Hobbitenango correctly can make the difference between spectacular volcano views and complete cloud cover. Here's what you need to know:
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Best Season: Dry Season (November-April)</h3>
          <p className="text-lg leading-relaxed mb-4">
            The <a href="/best-time-to-visit-antigua-guatemala" className="text-teal-600 hover:text-teal-700">best time to visit Antigua Guatemala</a> is during dry season, and this applies doubly to Hobbitenango. Clear skies mean unobstructed views of all three volcanoes, making your photos dramatically better.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            December through March offers the most consistent clear weather, though mornings are generally clearer than afternoons even during dry season.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Rainy Season (May-October): Proceed with Caution</h3>
          <p className="text-lg leading-relaxed mb-4">
            During rainy season, clouds frequently obscure the volcano views, especially in the afternoon. If you visit during this period, go as early as possible (8-10 AM) when skies are most likely to be clear.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            Check the weather forecast before committing to the trip. If it's heavily overcast or raining, consider postponing—the volcano views are the main attraction, and without them, the experience is significantly diminished.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Best Time of Day: Early Morning</h3>
          <p className="text-lg leading-relaxed mb-4">
            Arrive when the park opens (8:00 AM) or shortly after. Early morning offers:
          </p>

          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Clearest views before clouds roll in</li>
            <li>Fewer crowds at the giant hand viewpoint</li>
            <li>Better lighting for photos</li>
            <li>Cooler temperatures for hiking the trails</li>
          </ul>

          <p className="text-lg leading-relaxed mb-4">
            By mid-afternoon, clouds often obscure the volcanoes even during dry season. If you can only visit in the afternoon, you're taking a gamble on the views.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Weekday vs Weekend</h3>
          <p className="text-lg leading-relaxed mb-4">
            Weekdays are significantly less crowded than weekends, when local families and tour groups fill the park. If you want the giant hand viewpoint to yourself, visit on a weekday morning.
          </p>
        </section>

        {/* Pro Tips */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Pro Tips for Visiting Hobbitenango</h2>
          
          <div className="space-y-6">
            {proTips.map((tip, index) => (
              <div key={index} className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
                <h3 className="text-lg font-bold text-amber-900 mb-2">
                  <i className="ri-lightbulb-line mr-2"></i>
                  {tip.title}
                </h3>
                <p className="text-amber-900 leading-relaxed">{tip.tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Is It Worth It */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Is Hobbitenango Worth It?</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            Whether Hobbitenango is worth your time and money depends on your travel style, budget, and what you're hoping to experience in Antigua.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Hobbitenango is Worth It If You:</h3>
          <ul className="list-disc pl-6 mb-6 space-y-2 text-lg">
            <li>Enjoy unique photo opportunities and Instagram-worthy spots</li>
            <li>Want volcano views without doing a strenuous hike</li>
            <li>Are traveling with kids or family members who can't do intense hikes</li>
            <li>Appreciate whimsical, creative environments</li>
            <li>Have clear weather and can visit during dry season</li>
            <li>Don't mind paying for curated experiences</li>
          </ul>

          <h3 className="text-2xl font-bold mb-4 mt-8">Skip Hobbitenango If You:</h3>
          <ul className="list-disc pl-6 mb-6 space-y-2 text-lg">
            <li>Prefer natural, non-commercialized outdoor experiences</li>
            <li>Are on a very tight budget (free viewpoints like Cerro de la Cruz exist)</li>
            <li>Are visiting during rainy season with poor weather forecasts</li>
            <li>Have limited time and would rather prioritize <a href="/things-to-do-in-antigua-guatemala" className="text-teal-600 hover:text-teal-700">things to do in Antigua Guatemala</a> itself</li>
            <li>Aren't interested in posed photo opportunities</li>
          </ul>

          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango isn't trying to be a wilderness experience—it's an eco-park designed for fun, photos, and accessible volcano views. If you approach it with those expectations, most visitors find it enjoyable and worth the half-day excursion.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            However, if you're expecting pristine nature or authentic cultural experiences, you'll likely be disappointed. It's unabashedly touristy, and that's okay—just know what you're getting.
          </p>
        </section>

        {/* Comparison Table */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Hobbitenango vs Cerro de la Cruz</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            If you're deciding between Hobbitenango and Cerro de la Cruz (another popular viewpoint near Antigua), here's how they compare:
          </p>

          <div className="overflow-x-auto my-8">
            <table className="w-full border-collapse bg-white shadow-sm rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-teal-600 text-white">
                  <th className="px-4 py-3 text-left font-semibold">Feature</th>
                  <th className="px-4 py-3 text-left font-semibold">Hobbitenango</th>
                  <th className="px-4 py-3 text-left font-semibold">Cerro de la Cruz</th>
                </tr>
              </thead>
              <tbody>
                {hobbitenangoCerroComparison.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-4 py-3 font-medium">{item.feature}</td>
                    <td className="px-4 py-3">{item.hobbitenango}</td>
                    <td className="px-4 py-3">{item.cerroDeLaCruz}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-lg leading-relaxed mb-4">
            <strong>Bottom line:</strong> If you're on a budget or short on time, Cerro de la Cruz is the practical choice. If you want a more developed experience with food, facilities, and unique photo ops, Hobbitenango is worth the extra cost and travel time.
          </p>

          <p className="text-lg leading-relaxed mb-4">
            Many visitors do both—Cerro de la Cruz in the morning (it's walkable from Antigua) and Hobbitenango in the late morning or early afternoon as part of a <a href="/day-trips-from-antigua-guatemala" className="text-teal-600 hover:text-teal-700">day trip from Antigua Guatemala</a>.
          </p>
        </section>

        {/* Itinerary Ideas */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Hobbitenango + Antigua Itinerary Ideas</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango works well as part of a half-day or full-day itinerary. Here are some practical combinations:
          </p>

          <div className="space-y-6 my-8">
            {itineraryIdeas.map((idea, index) => (
              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-3 text-teal-700">{idea.time}</h3>
                <p className="text-gray-800 leading-relaxed">{idea.plan}</p>
              </div>
            ))}
          </div>

          <p className="text-lg leading-relaxed mb-4">
            If you're planning a longer stay in Antigua, check out our <a href="/antigua-guatemala-3-day-itinerary" className="text-teal-600 hover:text-teal-700">Antigua Guatemala 3-day itinerary</a> or <a href="/antigua-guatemala-2-week-itinerary" className="text-teal-600 hover:text-teal-700">2-week itinerary</a> for more ideas on how to structure your time.
          </p>
        </section>

        {/* Practical Tips */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Practical Tips for Your Visit</h2>
          
          <h3 className="text-2xl font-bold mb-4 mt-8">What to Wear</h3>
          <p className="text-lg leading-relaxed mb-4">
            Dress in layers. At 2,500 meters elevation, Hobbitenango is cooler than Antigua, especially in the morning and on windy days. Bring:
          </p>

          <ul className="list-disc pl-6 mb-6 space-y-2 text-lg">
            <li>Comfortable walking shoes (trails can be uneven)</li>
            <li>Light jacket or sweater</li>
            <li>Hat and sunglasses</li>
            <li>Sunscreen (UV is stronger at altitude)</li>
            <li>Avoid flip-flops or sandals</li>
          </ul>

          <h3 className="text-2xl font-bold mb-4 mt-8">Altitude Considerations</h3>
          <p className="text-lg leading-relaxed mb-4">
            While 2,500 meters isn't extreme altitude, some visitors may feel slightly winded, especially if coming from sea level. Take it easy, stay hydrated, and don't rush.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Cash or Card?</h3>
          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango accepts both cash and cards, but bring some Quetzales as backup. You'll also want cash for tips if you hire a private driver or take a shuttle.
          </p>

          <h3 className="text-2xl font-bold mb-4 mt-8">Safety</h3>
          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango is generally safe. The park is well-maintained and staffed. However, exercise normal caution:
          </p>

          <ul className="list-disc pl-6 mb-6 space-y-2 text-lg">
            <li>Watch your footing near viewpoints and swings (steep drop-offs)</li>
            <li>Supervise children closely</li>
            <li>Don't leave valuables unattended</li>
            <li>Be cautious on the winding road if driving yourself</li>
          </ul>

          <p className="text-lg leading-relaxed mb-4">
            For more general safety information about the area, see our guide on <a href="/is-antigua-guatemala-safe" className="text-teal-600 hover:text-teal-700">is Antigua Guatemala safe</a>.
          </p>
        </section>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl p-8 my-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Plan Your Antigua Trip?</h2>
          <p className="text-lg mb-6 max-w-2xl mx-auto">
            Get personalized recommendations, itinerary planning, and insider tips for your perfect Antigua Guatemala adventure.
          </p>
          <a
            href="/plan-my-antigua-trip"
            className="inline-block bg-white text-teal-700 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            Plan My Trip
          </a>
        </div>

        {/* Common Mistakes */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Common Mistakes to Avoid at Hobbitenango</h2>
          
          <div className="space-y-4">
            <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-lg">
              <h3 className="text-lg font-bold text-rose-900 mb-2">Going on a Cloudy Day</h3>
              <p className="text-rose-900 leading-relaxed">
                The volcano views are the main attraction. Check the weather forecast and don't visit if it's overcast or raining. You'll be disappointed.
              </p>
            </div>

            <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-lg">
              <h3 className="text-lg font-bold text-rose-900 mb-2">Arriving in the Afternoon</h3>
              <p className="text-rose-900 leading-relaxed">
                Clouds typically roll in by mid-afternoon, even during dry season. Go early for the best views and photos.
              </p>
            </div>

            <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-lg">
              <h3 className="text-lg font-bold text-rose-900 mb-2">Not Using Your Restaurant Credit</h3>
              <p className="text-rose-900 leading-relaxed">
                Your entrance fee includes Q50 for the restaurant. Use it! The food is good and the views from the dining area are excellent.
              </p>
            </div>

            <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-lg">
              <h3 className="text-lg font-bold text-rose-900 mb-2">Wearing Inappropriate Shoes</h3>
              <p className="text-rose-900 leading-relaxed">
                Flip-flops or sandals aren't ideal for the trails and viewpoints. Wear comfortable closed-toe shoes.
              </p>
            </div>

            <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-lg">
              <h3 className="text-lg font-bold text-rose-900 mb-2">Not Arranging Return Transport</h3>
              <p className="text-rose-900 leading-relaxed">
                If taking Uber or taxi, arrange your pickup time in advance. Rides aren't always available on-demand at the park.
              </p>
            </div>

            <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-lg">
              <h3 className="text-lg font-bold text-rose-900 mb-2">Rushing Through</h3>
              <p className="text-rose-900 leading-relaxed">
                Give yourself at least 2-3 hours to explore properly. There's more to see than just the giant hand viewpoint.
              </p>
            </div>
          </div>
        </section>

        {/* Who Should NOT Do This */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Who Should NOT Visit Hobbitenango</h2>
          
          <p className="text-lg leading-relaxed mb-4">
            Hobbitenango isn't for everyone. Consider skipping it if:
          </p>

          <ul className="list-disc pl-6 mb-6 space-y-2 text-lg">
            <li><strong>You're on a very tight budget:</strong> At Q100 entrance plus transport costs, there are free alternatives like Cerro de la Cruz that offer similar (though less developed) views.</li>
            <li><strong>You prefer authentic, non-commercialized experiences:</strong> Hobbitenango is designed for tourists and Instagram. If you want raw, natural Guatemala, look elsewhere.</li>
            <li><strong>You have mobility limitations:</strong> While not extremely strenuous, the park involves walking on uneven terrain and stairs. Some viewpoints may be challenging for those with mobility issues.</li>
            <li><strong>You're visiting during rainy season with poor weather:</strong> Without clear volcano views, the experience loses much of its appeal.</li>
            <li><strong>You only have 2-3 days in Antigua:</strong> With limited time, prioritize <a href="/acatenango-hike-from-antigua" className="text-teal-600 hover:text-teal-700">Acatenango volcano hike</a>, colonial Antigua itself, and perhaps one major day trip like Lake Atitlán.</li>
          </ul>

          <p className="text-lg leading-relaxed mb-4">
            That said, if you have 4+ days in Antigua, enjoy creative photo opportunities, and have good weather, Hobbitenango can be a fun addition to your itinerary.
          </p>
        </section>

        {/* FAQ Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-8">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-lg pr-4">{faq.question}</span>
                  <i className={`ri-arrow-${openFaqIndex === index ? 'up' : 'down'}-s-line text-xl text-teal-600 flex-shrink-0`}></i>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl p-8 my-12">
          <h2 className="text-3xl font-bold mb-4 text-center">Explore More Antigua Adventures</h2>
          <p className="text-lg mb-6 text-center max-w-2xl mx-auto">
            Hobbitenango is just one of many incredible experiences near Antigua. Discover volcano hikes, day trips, and complete itineraries.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/plan-my-antigua-trip"
              className="inline-block bg-teal-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              Plan My Trip
            </a>
            <a
              href="/acatenango-hike-from-antigua"
              className="inline-block bg-white text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Acatenango Volcano Hike
            </a>
            <a
              href="/free-antigua-itinerary-pdf"
              className="inline-block bg-white text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Free Itinerary PDF
            </a>
          </div>
        </div>

        {/* Related Guides */}
        <RelatedGuides guides={relatedGuides} />
      </div>

      <Footer />
    </>
  );
}